import {
  User,
  Settings,
  Sparkles,
  FileText,
  Code,
  Cpu,
  Sliders,
  Mic,
  Globe,
  Terminal,
  FolderOpen,
  Plug,
  Zap,
  Database,
  Workflow,
  Share2,
  Link2,
  Code2,
  MessageSquare,
  Mail,
  Phone,
  ExternalLink,
  QrCode,
} from "lucide-react"

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
}

interface FeatureShowcase {
  badge: string
  badgeIcon: React.ReactNode
  title: string
  description: string
  features: Feature[]
  mockup: React.ReactNode
  reversed?: boolean
}

/**
 * Profile section mockup component.
 * Displays a visual representation of the agent profile editor.
 */
function ProfileMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Avatar and Name */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5">
            <User className="h-8 w-8 text-primary" />
          </div>
          <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
            <Sparkles className="h-3 w-3" />
          </div>
        </div>
        <div className="flex-1">
          <div className="h-4 w-32 rounded bg-neutral-200" />
          <div className="mt-2 h-3 w-48 rounded bg-neutral-100" />
        </div>
      </div>

      {/* Description Field */}
      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4 text-neutral-400" />
          <div className="h-3 w-20 rounded bg-neutral-200" />
        </div>
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-neutral-200" />
            <div className="h-3 w-3/4 rounded bg-neutral-200" />
            <div className="h-3 w-5/6 rounded bg-neutral-100" />
          </div>
        </div>
      </div>

      {/* Code Editor Mockup */}
      <div className="rounded-lg bg-neutral-900 p-4">
        <div className="mb-2 flex items-center gap-2">
          <Code className="h-4 w-4 text-neutral-400" />
          <span className="text-xs text-neutral-400">System Instructions</span>
        </div>
        <div className="space-y-1.5 font-mono text-xs">
          <div className="text-purple-400">
            You are a helpful AI assistant...
          </div>
          <div className="text-neutral-500">
            # Guidelines
          </div>
          <div className="text-neutral-400">
            - Be concise and accurate
          </div>
          <div className="text-neutral-400">
            - Use markdown formatting
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Capabilities section mockup component.
 * Displays a visual representation of the capabilities configuration.
 */
function CapabilitiesMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Model Selector */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 flex-1 items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3">
          <Cpu className="h-4 w-4 text-neutral-400" />
          <span className="text-sm text-neutral-700">GPT-4o</span>
          <div className="ml-auto h-4 w-4 rounded bg-neutral-200" />
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50">
          <Sliders className="h-4 w-4 text-neutral-400" />
        </div>
      </div>

      {/* Toggle Switches */}
      <div className="space-y-4">
        {[
          { icon: <Mic className="h-4 w-4" />, label: "Text-to-Speech", active: true },
          { icon: <Globe className="h-4 w-4" />, label: "Web Search", active: true },
          { icon: <FileText className="h-4 w-4" />, label: "File Attachments", active: false },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-neutral-400">{item.icon}</div>
              <span className="text-sm text-neutral-700">{item.label}</span>
            </div>
            <div
              className={`h-5 w-9 rounded-full ${
                item.active ? "bg-primary" : "bg-neutral-200"
              }`}
            >
              <div
                className={`h-4 w-4 translate-y-0.5 rounded-full bg-white shadow transition-transform ${
                  item.active ? "translate-x-4" : "translate-x-0.5"
                }`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Tools Section */}
      <div className="mt-6 border-t border-neutral-100 pt-4">
        <div className="mb-3 text-xs font-medium uppercase tracking-wide text-neutral-500">
          Built-in Tools
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { icon: <Terminal className="h-4 w-4" />, label: "Terminal" },
            { icon: <FolderOpen className="h-4 w-4" />, label: "Files" },
            { icon: <Code className="h-4 w-4" />, label: "Code" },
          ].map((tool, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-1 rounded-lg border border-primary/20 bg-primary/5 p-2"
            >
              <div className="text-primary">{tool.icon}</div>
              <span className="text-xs text-neutral-600">{tool.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Connections section mockup component.
 * Displays a visual representation of integrations and triggers.
 */
function ConnectionsMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Integrations */}
      <div className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <Plug className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700">
            Integrations
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {["Slack", "Gmail", "Jira", "GitHub", "Notion", "Hubspot", "Stripe", "+12"].map(
            (name, i) => (
              <div
                key={i}
                className={`flex h-10 items-center justify-center rounded-lg border ${
                  name.startsWith("+")
                    ? "border-dashed border-neutral-300 bg-neutral-50 text-xs text-neutral-500"
                    : "border-neutral-200 bg-white"
                }`}
              >
                {name.startsWith("+") ? (
                  name
                ) : (
                  <div className="h-5 w-5 rounded bg-neutral-200" />
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Triggers */}
      <div className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <Zap className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700">Triggers</span>
        </div>
        <div className="space-y-2">
          {[
            { label: "On new email received", type: "Email" },
            { label: "On Slack message", type: "Slack" },
          ].map((trigger, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2"
            >
              <span className="text-sm text-neutral-700">{trigger.label}</span>
              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                {trigger.type}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Knowledge Base */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Database className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700">
            Knowledge Base
          </span>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
          <Workflow className="h-5 w-5 text-primary" />
          <div>
            <div className="text-sm font-medium text-neutral-700">
              Company Docs
            </div>
            <div className="text-xs text-neutral-500">
              1,234 documents indexed
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Publish & Share section mockup component.
 * Displays a visual representation of publishing and channel configuration.
 */
function PublishMockup() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
      {/* Direct Link */}
      <div className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <Link2 className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700">
            Direct Link
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
            <span className="text-sm text-neutral-600 truncate">
              https://taqat.ai/agent/sales-assistant
            </span>
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 bg-white">
            <ExternalLink className="h-4 w-4 text-neutral-400" />
          </div>
        </div>
      </div>

      {/* Embed Options */}
      <div className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <Code2 className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700">
            Embed Options
          </span>
        </div>
        <div className="flex gap-2">
          <div className="flex-1 rounded-lg border-2 border-primary bg-primary/5 p-3 text-center">
            <div className="mb-1 text-sm font-medium text-primary">Full Page</div>
            <div className="text-xs text-neutral-500">Embedded iframe</div>
          </div>
          <div className="flex-1 rounded-lg border border-neutral-200 bg-white p-3 text-center">
            <div className="mb-1 text-sm font-medium text-neutral-700">Bubble Chat</div>
            <div className="text-xs text-neutral-500">Floating widget</div>
          </div>
        </div>
      </div>

      {/* Channels */}
      <div className="mb-6">
        <div className="mb-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700">
            Channels
          </span>
        </div>
        <div className="space-y-2">
          {/* Slack - Connected */}
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#4A154B]">
                <span className="text-xs font-bold text-white">#</span>
              </div>
              <div>
                <div className="text-sm font-medium text-neutral-700">Slack</div>
                <div className="text-xs text-neutral-500">workspace.slack.com</div>
              </div>
            </div>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              Connected
            </span>
          </div>

          {/* WhatsApp */}
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#25D366]">
                <Phone className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-neutral-700">WhatsApp</div>
                <div className="text-xs text-neutral-500">Business API</div>
              </div>
            </div>
            <span className="text-xs font-medium text-primary">+ Connect</span>
          </div>

          {/* Email */}
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500">
                <Mail className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-neutral-700">Email</div>
                <div className="text-xs text-neutral-500">agent@mail.taqat.ai</div>
              </div>
            </div>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              Active
            </span>
          </div>
        </div>
      </div>

      {/* QR Code */}
      <div className="flex items-center gap-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
        <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-white border border-neutral-200">
          <QrCode className="h-10 w-10 text-neutral-400" />
        </div>
        <div>
          <div className="text-sm font-medium text-neutral-700">QR Code</div>
          <div className="text-xs text-neutral-500">
            Scan to access your agent instantly
          </div>
        </div>
      </div>
    </div>
  )
}

const showcases: FeatureShowcase[] = [
  {
    badge: "Profile & Instructions",
    badgeIcon: <Settings className="h-3.5 w-3.5" />,
    title: "Define Your Agent's Identity",
    description:
      "Give your AI agent a unique personality and clear instructions. Customize everything from its name and avatar to detailed system prompts that guide its behavior.",
    features: [
      {
        icon: <User className="h-5 w-5" />,
        title: "Custom Avatar",
        description: "Upload or generate unique visual identity",
      },
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: "AI-Enhanced Descriptions",
        description: "Let AI help craft perfect agent descriptions",
      },
      {
        icon: <FileText className="h-5 w-5" />,
        title: "Markdown Instructions",
        description: "Rich system prompts with full markdown support",
      },
      {
        icon: <Code className="h-5 w-5" />,
        title: "JSON Schema",
        description: "Define structured output formats",
      },
    ],
    mockup: <ProfileMockup />,
  },
  {
    badge: "Capabilities",
    badgeIcon: <Cpu className="h-3.5 w-3.5" />,
    title: "Configure AI Capabilities",
    description:
      "Fine-tune your agent's intelligence with multi-provider model selection, advanced settings, and powerful built-in tools for maximum productivity.",
    features: [
      {
        icon: <Cpu className="h-5 w-5" />,
        title: "Multi-Provider Models",
        description: "OpenAI, Anthropic, DeepSeek, and more",
      },
      {
        icon: <Sliders className="h-5 w-5" />,
        title: "Advanced Settings",
        description: "Temperature, tokens, penalties control",
      },
      {
        icon: <Mic className="h-5 w-5" />,
        title: "Rich Features",
        description: "TTS, attachments, web search built-in",
      },
      {
        icon: <Terminal className="h-5 w-5" />,
        title: "Built-in Tools",
        description: "Terminal, file manager, code execution",
      },
    ],
    mockup: <CapabilitiesMockup />,
    reversed: true,
  },
  {
    badge: "Connections",
    badgeIcon: <Plug className="h-3.5 w-3.5" />,
    title: "Connect Everything",
    description:
      "Seamlessly integrate your agent with 50+ third-party services, configure event-based triggers, and connect to your knowledge bases for contextual intelligence.",
    features: [
      {
        icon: <Plug className="h-5 w-5" />,
        title: "50+ Integrations",
        description: "Slack, Gmail, Jira, GitHub, and more",
      },
      {
        icon: <Zap className="h-5 w-5" />,
        title: "Event Triggers",
        description: "Automate actions based on events",
      },
      {
        icon: <Database className="h-5 w-5" />,
        title: "Knowledge Base",
        description: "Connect your documents and data",
      },
      {
        icon: <Workflow className="h-5 w-5" />,
        title: "Seamless Flow",
        description: "Data flows naturally between systems",
      },
    ],
    mockup: <ConnectionsMockup />,
  },
  {
    badge: "Publish & Share",
    badgeIcon: <Share2 className="h-3.5 w-3.5" />,
    title: "Deploy Everywhere",
    description:
      "Publish your agent with one click and share it across multiple channels. Embed on your website, connect to Slack, WhatsApp, Email, or share via direct link.",
    features: [
      {
        icon: <Link2 className="h-5 w-5" />,
        title: "Shareable Link",
        description: "Instant access via custom URL",
      },
      {
        icon: <Code2 className="h-5 w-5" />,
        title: "Embed Options",
        description: "Full page iframe or bubble chat widget",
      },
      {
        icon: <MessageSquare className="h-5 w-5" />,
        title: "Multi-Channel",
        description: "Slack, WhatsApp, Email, Voice",
      },
      {
        icon: <QrCode className="h-5 w-5" />,
        title: "QR Code",
        description: "Scan to connect on mobile instantly",
      },
    ],
    mockup: <PublishMockup />,
    reversed: true,
  },
]

/**
 * Features section displaying the four main agent builder panels.
 * Uses alternating left/right layout with mockups and feature lists.
 */
export function FeaturesSection() {
  return (
    <section className="bg-neutral-50 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Everything You Need to Build AI Agents
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            Our visual builder gives you complete control over every aspect of
            your AI agents, from personality to integrations.
          </p>
        </div>

        {/* Feature Showcases */}
        <div className="space-y-24">
          {showcases.map((showcase, index) => (
            <div
              key={index}
              className={`grid gap-12 lg:grid-cols-2 lg:gap-16 ${
                showcase.reversed ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Text Content */}
              <div
                className={`flex flex-col justify-center ${
                  showcase.reversed ? "lg:order-2" : ""
                }`}
              >
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
                  {showcase.badgeIcon}
                  {showcase.badge}
                </div>
                <h3 className="mb-4 text-2xl font-bold text-neutral-900 lg:text-3xl">
                  {showcase.title}
                </h3>
                <p className="mb-8 text-neutral-600">{showcase.description}</p>

                {/* Feature List */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {showcase.features.map((feature, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">
                          {feature.title}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mockup */}
              <div className={showcase.reversed ? "lg:order-1" : ""}>
                {showcase.mockup}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
